import React from 'react';
import { 
  Search, 
  RotateCcw, 
  Building2, 
  Calendar, 
  BookOpen, 
  Clock, 
  AlertCircle,
  Copy,
  ExternalLink,
  ShieldCheck,
  FileSpreadsheet
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useIndiceSociedades, TIPOS_CONTRATO } from '../../hooks/useIndiceSociedades.js';

// Estilos según el tipo de acto registral
const BADGE_STYLES = {
  'Constitución': { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
  'Modificación': { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  'Transformación': { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe' },
  'Disolución': { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3' },
};

export default function ConsultaSociedades() {
  const {
    nombreBusqueda,
    setNombreBusqueda,
    tipoContratoFiltro,
    setTipoContratoFiltro,
    inscripciones,
    cargando,
    error,
    haBuscado,
    conteoPorTipo,
    consultarSociedad,
    limpiar,
  } = useIndiceSociedades();

  const handleSubmit = (e) => {
    e.preventDefault();
    consultarSociedad();
  };

  const handleCambiarFiltro = (tipo) => {
    setTipoContratoFiltro(tipo);
    if (haBuscado && nombreBusqueda.trim()) {
      consultarSociedad(nombreBusqueda, tipo);
    }
  };

  const copiarCitaRegistral = (item) => {
    const fsTexto = item.fojas_termino && item.fojas_termino !== item.fojas 
      ? `Fs. ${item.fojas} a ${item.fojas_termino}` 
      : `Fs. ${item.fojas}`;
    const texto = `${fsTexto} N° ${item.numero} Año ${item.ano} (${item.tipo_de_contrato} - ${item.nombre_de_la_sociedad})`;
    navigator.clipboard.writeText(texto);
    toast.success('Cita registral copiada al portapapeles');
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem 1.25rem' }}>
      
      {/* ── Encabezado Principal ────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '0.5rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, #003366, #0284c7)',
            padding: '12px',
            borderRadius: '12px',
            color: '#fff',
            display: 'flex',
            boxShadow: '0 4px 12px rgba(0, 51, 102, 0.2)'
          }}>
            <Building2 size={28} />
          </div>
          <div>
            <h1 style={{ fontSize: '1.85rem', fontWeight: 800, margin: 0, color: '#0f172a', letterSpacing: '-0.5px' }}>
              Índice de Sociedades
            </h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.95rem' }}>
              Registro de Comercio: Consulta inscripciones históricas por razón social y tipo de contrato
            </p>
          </div>
        </div>
      </div>

      {/* ── Barra de Búsqueda y Filtros Dinámicos ─────────────────────────── */}
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: '1.75rem',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.04)',
        marginBottom: '2rem'
      }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 340px', position: 'relative' }}>
            <Search 
              size={20} 
              style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} 
            />
            <input
              type="text"
              placeholder="Ingresa la razón social (ej: Agrícola El Roble, Inversiones Los Andes SpA)..."
              value={nombreBusqueda}
              onChange={(e) => setNombreBusqueda(e.target.value)}
              style={{
                width: '100%',
                padding: '0.85rem 1rem 0.85rem 2.8rem',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '1rem',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#003366'}
              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="submit"
              disabled={cargando}
              style={{
                background: 'linear-gradient(135deg, #003366, #002244)',
                color: '#fff',
                padding: '0.85rem 1.75rem',
                borderRadius: '10px',
                border: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 10px rgba(0, 51, 102, 0.25)',
                transition: 'opacity 0.2s'
              }}
            >
              {cargando ? <Clock className="spinner" size={18} /> : <Search size={18} />}
              <span>{cargando ? 'Buscando...' : 'Buscar Sociedad'}</span>
            </button>

            <button
              type="button"
              onClick={limpiar}
              disabled={cargando || (!nombreBusqueda && !haBuscado)}
              style={{
                background: '#f8fafc',
                color: '#475569',
                padding: '0.85rem 1.25rem',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              <RotateCcw size={16} />
              <span>Limpiar</span>
            </button>
          </div>
        </form>

        {/* Píldoras de Filtro Dinámico */}
        <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Tipo de Contrato:
          </span>
          {TIPOS_CONTRATO.map((tipo) => {
            const activo = tipoContratoFiltro === tipo;
            const count = tipo === 'TODOS' ? inscripciones.length : (conteoPorTipo[tipo] || 0);

            return (
              <button
                key={tipo}
                type="button"
                onClick={() => handleCambiarFiltro(tipo)}
                style={{
                  padding: '0.45rem 0.95rem',
                  borderRadius: '20px',
                  border: activo ? '1.5px solid #003366' : '1px solid #e2e8f0',
                  background: activo ? '#003366' : '#f8fafc',
                  color: activo ? '#ffffff' : '#475569',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>{tipo}</span>
                {haBuscado && count > 0 && (
                  <span style={{
                    padding: '2px 7px',
                    borderRadius: '10px',
                    background: activo ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                    fontSize: '0.75rem',
                    fontWeight: 700
                  }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Estado de Carga (Loading) ─────────────────────────────────────── */}
      {cargando && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
          <div className="spinner spinner-lg" style={{ margin: '0 auto 1.25rem' }} />
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.2rem' }}>Consultando Libros de Comercio...</h3>
          <p style={{ color: '#64748b', fontSize: '0.95rem', marginTop: '0.5rem' }}>
            Recuperando citas registrales de constituciones, modificaciones, transformaciones y disoluciones.
          </p>
        </div>
      )}

      {/* ── Estado de Error ──────────────────────────────────────────────── */}
      {error && !cargando && (
        <div style={{
          padding: '1.25rem 1.5rem',
          borderRadius: '12px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#991b1b',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <AlertCircle size={24} style={{ flexShrink: 0 }} />
          <div>
            <strong style={{ fontSize: '0.95rem' }}>No fue posible completar la consulta:</strong>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.9rem', color: '#b91c1c' }}>{error}</p>
          </div>
        </div>
      )}

      {/* ── Estado Vacío Inicial ─────────────────────────────────────────── */}
      {!haBuscado && !cargando && (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: '#f8fafc',
          borderRadius: '16px',
          border: '2px dashed #cbd5e1'
        }}>
          <BookOpen size={48} style={{ color: '#94a3b8', margin: '0 auto 1rem' }} />
          <h3 style={{ margin: 0, color: '#334155', fontSize: '1.25rem' }}>Ingresa una Razón Social para Consultar</h3>
          <p style={{ color: '#64748b', fontSize: '0.95rem', maxWidth: 500, margin: '0.5rem auto 0', lineHeight: 1.5 }}>
            El sistema buscará en los registros de comercio las inscripciones de <strong>Constitución</strong>, <strong>Modificación</strong>, <strong>Transformación</strong> y <strong>Disolución</strong> con sus correspondientes fojas, número y año.
          </p>
        </div>
      )}

      {/* ── Sin Resultados ──────────────────────────────────────────────── */}
      {haBuscado && !cargando && inscripciones.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '3.5rem 2rem',
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0'
        }}>
          <AlertCircle size={44} style={{ color: '#f59e0b', margin: '0 auto 0.85rem' }} />
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.2rem' }}>Sin Inscripciones Encontradas</h3>
          <p style={{ color: '#64748b', fontSize: '0.95rem', maxWidth: 460, margin: '0.5rem auto 0', lineHeight: 1.5 }}>
            No se hallaron registros comerciales para la búsqueda <strong>"{nombreBusqueda}"</strong>
            {tipoContratoFiltro !== 'TODOS' && ` con filtro de acto "${tipoContratoFiltro}"`}.
          </p>
        </div>
      )}

      {/* ── Resultados e Historial de Inscripciones ──────────────────────── */}
      {haBuscado && !cargando && inscripciones.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Banner de Identificación de la Sociedad */}
          <div style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            padding: '1.25rem 1.75rem',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', fontWeight: 700 }}>
                Razón Social Identificada
              </span>
              <h2 style={{ fontSize: '1.4rem', margin: '0.2rem 0 0', color: '#0f172a', fontWeight: 700 }}>
                {inscripciones[0]?.nombre_de_la_sociedad || nombreBusqueda}
              </h2>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Total Inscripciones</span>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#003366' }}>
                  {inscripciones.length}
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de Resultados Registrales */}
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ 
              padding: '1rem 1.5rem', 
              borderBottom: '1px solid #f1f5f9', 
              background: '#fafbfc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#334155', fontWeight: 700 }}>
                Historial Registral Cronológico
              </h3>
              <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                Ordenado por Año y Número de Inscripción
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.92rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                    <th style={{ padding: '0.9rem 1.25rem', fontWeight: 700, width: '18%' }}>Tipo de Contrato</th>
                    <th style={{ padding: '0.9rem 1.25rem', fontWeight: 700, width: '34%' }}>Sociedad & Intervinientes</th>
                    <th style={{ padding: '0.9rem 1.25rem', fontWeight: 700, width: '22%' }}>Cita Registral</th>
                    <th style={{ padding: '0.9rem 1.25rem', fontWeight: 700, width: '14%' }}>Fecha Asiento</th>
                    <th style={{ padding: '0.9rem 1.25rem', fontWeight: 700, width: '12%', textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {inscripciones.map((item, idx) => {
                    const badge = BADGE_STYLES[item.tipo_de_contrato] || {
                      bg: '#f1f5f9', text: '#334155', border: '#cbd5e1', icon: '📄'
                    };

                    const fojaRango = item.fojas_termino && item.fojas_termino !== item.fojas 
                      ? `${item.fojas} a ${item.fojas_termino}` 
                      : `${item.fojas}`;

                    return (
                      <tr 
                        key={item.id || idx} 
                        style={{ 
                          borderBottom: '1px solid #f1f5f9',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#fcfdfe'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        {/* Acto / Tipo de Contrato */}
                        <td style={{ padding: '1rem 1.25rem' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            background: badge.bg,
                            color: badge.text,
                            border: `1px solid ${badge.border}`
                          }}>
                            <span>{item.tipo_de_contrato}</span>
                          </span>
                        </td>

                        {/* Sociedad & Intervinientes */}
                        <td style={{ padding: '1rem 1.25rem' }}>
                          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem', marginBottom: '4px' }}>
                            {item.nombre_de_la_sociedad}
                          </div>
                          {item.intervinientes && item.intervinientes.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {item.intervinientes.map((int, iIdx) => (
                                <span
                                  key={int.id || iIdx}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    padding: '2px 7px',
                                    borderRadius: '6px',
                                    fontSize: '0.74rem',
                                    color: '#334155'
                                  }}
                                >
                                  <strong style={{ color: '#003366', textTransform: 'uppercase', fontSize: '0.68rem' }}>
                                    {int.rol}:
                                  </strong>{' '}
                                  <span>{int.nombre_completo}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>

                        {/* Cita Registral (Foja, Número, Año) */}
                        <td style={{ padding: '1rem 1.25rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{
                              fontWeight: 700,
                              color: '#0f172a',
                              background: '#f1f5f9',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              fontSize: '0.88rem'
                            }}>
                              Fs. {fojaRango}
                            </span>
                            <span style={{ color: '#94a3b8' }}>•</span>
                            <span style={{ fontWeight: 700, color: '#334155', fontSize: '0.88rem' }}>
                              N° {item.numero}
                            </span>
                            <span style={{ color: '#94a3b8' }}>•</span>
                            <span style={{
                              fontWeight: 700,
                              color: '#003366',
                              background: '#e0f2fe',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              fontSize: '0.88rem'
                            }}>
                              Año {item.ano}
                            </span>
                          </div>
                        </td>

                        {/* Fecha */}
                        <td style={{ padding: '1rem 1.25rem', color: '#64748b' }}>
                          {item.fecha_inscripcion ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem' }}>
                              <Calendar size={14} style={{ color: '#94a3b8' }} />
                              <span>{item.fecha_inscripcion}</span>
                            </div>
                          ) : (
                            <span style={{ fontStyle: 'italic', color: '#94a3b8', fontSize: '0.85rem' }}>
                              Registrado en {item.ano}
                            </span>
                          )}
                        </td>

                        {/* Acciones */}
                        <td style={{ padding: '1rem 1.25rem', textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => copiarCitaRegistral(item)}
                            title="Copiar cita registral para certificados o minutas"
                            style={{
                              background: 'transparent',
                              border: '1px solid #cbd5e1',
                              color: '#475569',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#003366';
                              e.currentTarget.style.color = '#003366';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#cbd5e1';
                              e.currentTarget.style.color = '#475569';
                            }}
                          >
                            <Copy size={13} />
                            <span>Copiar Cita</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

    </div>
  );
}
